import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Lock,
  Settings,
  Layers,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { PageItem } from "./types";

interface SortablePageItemProps {
  page: PageItem;
  subdomain?: string;
  isFirst: boolean;
  isLast: boolean;
  onEdit: (page: PageItem) => void;
  onManageSections: (page: PageItem) => void;
  onDelete: (page: PageItem) => void;
  onToggleVisibility: (page: PageItem) => void;
  onMoveUp?: (page: PageItem) => void;
  onMoveDown?: (page: PageItem) => void;
  disabled?: boolean;
}

export function SortablePageItem({
  page,
  subdomain,
  isFirst,
  isLast,
  onEdit,
  onManageSections,
  onDelete,
  onToggleVisibility,
  onMoveUp,
  onMoveDown,
  disabled,
}: SortablePageItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: page.id,
    disabled: page.isHome || disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const pagePath = page.isHome ? "" : page.slug;
  const fullPublicUrl = subdomain
    ? `https://${subdomain}.profolio.lk/${pagePath}`
    : `/${pagePath}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex items-center justify-between p-3.5 rounded-xl border transition-all ${
        page.isHome
          ? "border-primary/30 bg-primary/[0.02] dark:bg-primary/[0.04] shadow-xs"
          : "border-border bg-card hover:border-border/80 shadow-xs"
      }`}
    >
      {/* Left: Drag Handle & Page Identity */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {page.isHome ? (
          <div
            className="flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground/60 select-none cursor-default"
            title="The Home page is permanently locked at the primary position."
          >
            <Lock className="h-4 w-4" />
          </div>
        ) : (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="flex items-center justify-center h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted cursor-grab active:cursor-grabbing touch-none select-none transition-colors"
            title="Drag to reorder page hierarchy"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-foreground truncate">
              {page.title}
            </h3>

            {page.isHome ? (
              <Badge variant="default" className="text-[10px] font-bold px-1.5 py-0 bg-primary/90">
                Home / Root
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 text-muted-foreground">
                /{page.slug}
              </Badge>
            )}

            {/* Visibility Indicator */}
            {page.isHome ? (
              <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Public
              </span>
            ) : page.visible ? (
              <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Visible
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Draft / Hidden
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {page.navigationLabel && page.navigationLabel !== page.title && (
              <span className="truncate">Nav: &ldquo;{page.navigationLabel}&rdquo;</span>
            )}
            <span className="flex items-center gap-1">
              <Layers className="h-3 w-3 shrink-0" />
              {page.sectionCount} {page.sectionCount === 1 ? "section" : "sections"}
            </span>
          </div>
        </div>
      </div>

      {/* Right: Quick Actions */}
      <div className="flex items-center gap-1 ml-3 shrink-0">
        {/* Accessible Reorder Arrows (Non-home) */}
        {!page.isHome && (
          <div className="hidden sm:flex items-center gap-0.5 mr-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isFirst || disabled}
              onClick={() => onMoveUp?.(page)}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="Move page up in navigation"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isLast || disabled}
              onClick={() => onMoveDown?.(page)}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              title="Move page down in navigation"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* Quick Visibility Toggle (Non-home) */}
        {!page.isHome && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onToggleVisibility(page)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
            title={page.visible ? "Hide page from navigation" : "Show page in navigation"}
          >
            {page.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-amber-500" />}
          </Button>
        )}

        {/* Manage Sections */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onManageSections(page)}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          title="Manage assigned sections"
        >
          <Layers className="h-4 w-4" />
        </Button>

        {/* Settings */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onEdit(page)}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          title="Page settings & SEO"
        >
          <Settings className="h-4 w-4" />
        </Button>

        {/* Delete (Non-home) */}
        {!page.isHome && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDelete(page)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            title="Delete page"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}

        {/* Public / Preview Link */}
        <a
          href={fullPublicUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Open public page in new tab"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}
